import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import { stashSignOutNotice } from './sessionExpiry';
import { captureFunnelEvent } from '../services/telemetry';

/**
 * Global 401 recovery on the default axios instance (every API call site).
 *
 * Force-refresh the ID token and retry once. Sign out and hard-navigate to
 * `/sign-in` only when Firebase tells us the credentials are gone for good —
 * never a guest, and never on a failure that might just be the network.
 */

interface RetryableConfig extends InternalAxiosRequestConfig {
  /** Namespaced so it can't collide with a caller's own retry bookkeeping. */
  _pmlAuthRetried?: boolean;
}

/**
 * Firebase error codes that mean the credentials themselves are gone, so no
 * amount of retrying will help.
 *
 * Everything else — `auth/network-request-failed`, `auth/too-many-requests`, and
 * notably `auth/internal-error`, which the SDK uses as a catch-all for any
 * unexpected backend response — says only that we could not REACH Firebase to
 * find out. Treating those as a dead session is how you sign out a paying
 * subscriber over a flaky connection, which is the same damage as the bug this
 * whole file exists to fix. The default must be to leave the session alone.
 */
const TERMINAL_REFRESH_CODES = new Set([
  'auth/user-token-expired',
  'auth/invalid-user-token',
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/user-signed-out',
]);

type RefreshOutcome =
  /** A usable token: replay the request with it. */
  | { kind: 'refreshed'; token: string }
  /** Credentials are unrecoverable: end the session and explain why. */
  | { kind: 'dead'; code: string | null }
  /** Could not find out: fail the request, keep the session. */
  | { kind: 'unavailable'; code: string | null };

let installed = false;
/** Set once we commit to leaving the page, to keep parallel 401s to one redirect. */
let signingOut = false;
/** Shared so a burst of parallel 401s triggers ONE token refresh, not one each. */
let refreshInFlight: Promise<RefreshOutcome> | null = null;

function bearerOf(config: InternalAxiosRequestConfig): unknown {
  return config.headers?.Authorization ?? config.headers?.authorization;
}

function reasonOf(error: AxiosError): string | null {
  const data = error.response?.data;
  if (data && typeof data === 'object' && 'reason' in data) {
    const reason = (data as { reason?: unknown }).reason;
    return typeof reason === 'string' ? reason : null;
  }
  return null;
}

/** Force a real refresh-token round-trip, classifying any failure. */
function refreshToken(): Promise<RefreshOutcome> {
  if (refreshInFlight) return refreshInFlight;

  const pending = (async (): Promise<RefreshOutcome> => {
    const user = auth.currentUser;
    // Already signed out: nothing to refresh, and nothing left to end.
    if (!user) return { kind: 'unavailable', code: null };
    try {
      // `true` is the whole point: the cached ID token is what just got
      // rejected, so asking for it again would replay the same failure.
      return { kind: 'refreshed', token: await user.getIdToken(true) };
    } catch (err) {
      const code = (err as { code?: string })?.code ?? null;
      return TERMINAL_REFRESH_CODES.has(code ?? '')
        ? { kind: 'dead', code }
        : { kind: 'unavailable', code };
    }
  })();

  refreshInFlight = pending;
  void pending.finally(() => {
    if (refreshInFlight === pending) refreshInFlight = null;
  });
  return pending;
}

/** `wasGuest` is sampled before the refresh — see `installAuthRecovery`. */
async function endDeadSession(
  reason: string | null,
  refreshCode: string | null,
  wasGuest: boolean
): Promise<void> {
  // Never sign out a guest. Their anonymous UID is the ONLY handle on the
  // project they just uploaded — signing out abandons it permanently, which
  // would turn a recoverable error into data loss. A guest simply sees the
  // failed request.
  if (wasGuest) return;

  // Already on the sign-in screen: redirecting again would reload in a loop.
  if (signingOut || window.location.pathname.startsWith('/sign-in')) return;
  signingOut = true;

  captureFunnelEvent('session_signed_out', {
    reason: reason ?? 'unknown',
    refresh_error_code: refreshCode,
    source: 'axios_401_interceptor',
  });
  stashSignOutNotice(reason);

  try {
    await signOut(auth);
  } catch {
    // Even if Firebase can't clear local state, get the user to a screen where
    // they can act.
  }
  // A full navigation rather than a router push, so every hook and cache still
  // holding the dead session's data is torn down with the document.
  window.location.assign('/sign-in');
}

export function installAuthRecovery(): void {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetryableConfig | undefined;

      if (error.response?.status !== 401 || !config) {
        return Promise.reject(error);
      }
      // A second 401 after a successful refresh is NOT a token problem, so
      // retrying again or signing out would be wrong (and could loop). Let the
      // caller handle it.
      if (config._pmlAuthRetried) return Promise.reject(error);
      // Requests that never carried a token can't be fixed by refreshing one,
      // and a 401 from an endpoint we called anonymously must not sign anyone out.
      if (!bearerOf(config)) return Promise.reject(error);

      config._pmlAuthRetried = true;
      const reason = reasonOf(error);

      // Snapshot the identity BEFORE attempting the refresh. Firebase's own
      // behaviour when it rejects a refresh is inconsistent — its session docs
      // say the user "is signed out", while clients report `currentUser`
      // surviving — so by the time we know the session is dead, `currentUser`
      // may already be null. Reading it then would make a real user look like a
      // signed-out one and skip both the explanation and the redirect, which is
      // exactly the silently-broken session this file exists to prevent.
      const user = auth.currentUser;
      // Nothing signed in: the request was doomed for a reason we can't repair.
      if (!user) return Promise.reject(error);
      const wasGuest = user.isAnonymous;

      const outcome = await refreshToken();
      if (outcome.kind === 'refreshed') {
        config.headers.Authorization = `Bearer ${outcome.token}`;
        return axios(config);
      }
      // We couldn't establish that the credentials are gone, only that we
      // couldn't check. Fail this request and leave the session intact.
      if (outcome.kind === 'unavailable') return Promise.reject(error);

      await endDeadSession(reason, outcome.code, wasGuest);
      return Promise.reject(error);
    }
  );
}
